import React from 'react';
import {GetStaticProps} from 'next';
import {ParsedUrlQuery} from 'querystring';

/* Data */
import headerData from '@integrations/preval/headerdata.preval';

/* Components */
import CategoryPageContainer from '@scenes/ServicesCategoryPage';
import {getLayout} from '@components/PageLayouts/Layout/Standard';

/* Apis */
import {skus, reviews} from '@api/apis';

/* Constants */
import {PAGE_TYPE_CATEGORY} from '@constants/pages';
import {SEVEN_DAYS} from '@constants/CacheRevalidate';

/* Types */
import {Sku} from 'types/sku.types';
import {Children} from 'types/base.types';
import {TServicesCategoryProps} from 'types/serviceCategory.types';
import {TActiveGroup, TServiceCardGroup, TServiceCategory} from '@scenes/ServicesCategoryPage/serviceCategoryPage.types';

/*
 * Our base Services Sku Page. The page proper is broken out just in case we ever need to
 * house that container in another layout.
 * */
function ServicesCategoryPage(props: TServicesCategoryProps): Children {
  return <CategoryPageContainer {...props} />;
}

ServicesCategoryPage.pageAttributes = {page: PAGE_TYPE_CATEGORY};
ServicesCategoryPage.Layout = getLayout({promo: true, footer: true});

export default ServicesCategoryPage;

/**
 * ---------------------------------------------------------------------------------------
 * Static Generation
 * @param params
 * ---------------------------------------------------------------------------------------
 */
interface StaticParams extends ParsedUrlQuery {
  seoName: string;
}

/**
 * - Get sku data
 * - Get reviews for sku
 * - Get routes for navigation (predone for rank/column)
 *
 * @param {GetStaticPropsContext<ParsedUrlQuery, PreviewData>} context
 * @returns {Promise<*>}
 */
type TItem = {
  id: string;
  name: string;
  seo_name: string;
  skus: Sku[];
};

export const getStaticProps: GetStaticProps = async context => {
  const {categoryName} = context.params as StaticParams;
  const {data} = await skus.all();
  const review = await reviews.list();

  /* Methods */
  /**
   * This method will return the sub category default sku grid: Not associated with a sub category
   * Usually a single sku outsid a sub category
   *
   * @param {TServiceCategory} currentPage
   * @param {TServiceCardGroup | TServiceCardGroup[]} currentPage
   * @returns {{skus: (TServiceCardGroup | FlatArray<TServiceCardGroup, -1>)[], name: string, id: number}[]}
   */
  const getSubCategorySkuGridBySku = (currentPage: TServiceCardGroup | TServiceCardGroup[]) =>
    [currentPage].flat().map(sku => ({
      name: 'Select a service',
      id: sku?.id || null,
      skus: [sku],
    }));

  /**
   * This method will return the sub category sku grid
   * @param {TServiceCategory} currentPage
   * @returns {{types: TCategoryTypes[] | {skus: (TServiceCardGroup | FlatArray<TServiceCardGroup, -1>)[], name: string, id: number}[], name: string, id: string, seoName: string}[] | undefined}
   */
  const getSubCategorySkuGrid = (currentPage: TServiceCategory) =>
    currentPage?.subCategories.map(subCategory => ({
      name: subCategory.name,
      id: subCategory.name,
      seoName: subCategory.seoName,
      types: subCategory.types || getSubCategorySkuGridBySku(subCategory.sku),
    }));

  /**
   * This method will return the default sku grid when the sku has no sub category
   * @param {TServiceCategory} currentPage
   * @returns {[{types: {skus: TServiceCardGroup[] | undefined, name: string, id: string}[], name: string}]}
   */
  const getSkuGrids = (currentPage: TServiceCategory) => [
    {name: currentPage?.name, types: [{name: 'Select a service', skus: currentPage?.skus, id: currentPage?.name || null}]},
  ];

  /* Base Constants */
  const servicesCompleted = {
    'tv-mounting': '98,746',
    'smart-home': '152,616',
    networking: '33,601',
    'television-and-audio': '40,885',
    mobile: '3,967',
    'home-security': 0,
    computers: '101,772',
    'around-the-home': 0,
    'home-cleaning': 0,
  };
  let category = {};

  data?.routes?.service_categories.forEach((item: TItem) => {
    if (item.seo_name === categoryName) {
      // 1. Merge all the columns into a single array
      const allColumns = headerData.servicesMenu.map(column => column.categories).flat();
      // 2. extract current page from the category
      const currentPage = allColumns.find(column => column.id === item.id);
      // 3. create the tabs array. Name, id
      const tabGroupings: TActiveGroup[] | any = currentPage?.subCategories ? getSubCategorySkuGrid(currentPage) : getSkuGrids(currentPage);

      category = {
        reviews: review?.data?.reviews || [],
        id: item.id,
        name: currentPage?.name || '',
        seoName: categoryName,
        servicesCompletedCount: servicesCompleted[categoryName] || 0,
        currentPage,
        tabGroupings,
      };
    }
  });

  return {
    props: {
      category,
    },
    revalidate: SEVEN_DAYS,
  };
};

/**
 * -------------------------------------------------------------------------
 * Static Path Generation
 * - Get navigatable service skus
 * - Pluck out just the seoName for pathing for routes
 *
 * @returns {Promise<{paths: RouteSkuParams<string>[] | undefined; fallback: boolean}>}
 */
type TRouteSkuParams = {
  params: {
    categoryName: string;
  };
};

export async function getStaticPaths<GetStaticPaths>() {
  const {data} = await skus.all();

  const allSkus = data?.routes?.service_categories.reduce((skusRoute: TRouteSkuParams[], item: {id: number; seo_name: string}) => {
    const allColumns = headerData.servicesMenu.map(column => column.categories).flat();
    const currentPage = allColumns.find(column => column.id === item.id);
    const stringSeoName = String(item.seo_name);

    if (!stringSeoName || stringSeoName === 'undefined' || !currentPage) return skusRoute;

    skusRoute.push({
      params: {
        categoryName: stringSeoName,
      },
    });

    return skusRoute;
  }, []);

  return {
    paths: allSkus,
    fallback: true,
  };
}
